import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Paper, 
  Alert, 
  Button,
  Container
} from '@mui/material';
import { 
  Cancel as CancelIcon, 
  Error as ErrorIcon
} from '@mui/icons-material';
import { getApiBaseUrl } from '../../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

const TeamInvitationReject: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleReject = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setError('Invalid invitation link. Missing token.');
        setLoading(false);
        return;
      }

      try {
        // Call backend API to reject the invitation
        const response = await axios.get(
          `${API_BASE_URL}/api/team/invitation/reject`,
          {
            params: { token },
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          setSuccess(true);
        } else {
          setError(response.data.message || 'Failed to decline invitation');
        }
      } catch (err: any) {
        console.error('Error rejecting invitation:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to decline invitation';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    handleReject();
  }, [searchParams]);

  if (loading) {
    return (
      <Container maxWidth="sm" style={{ marginTop: '100px' }}>
        <Paper style={{ padding: '40px', textAlign: 'center' }}>
          <CircularProgress style={{ marginBottom: '20px' }} />
          <Typography variant="h6">Processing your response...</Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" style={{ marginTop: '100px' }}>
        <Paper style={{ padding: '40px', textAlign: 'center' }}>
          <ErrorIcon color="error" style={{ fontSize: 64, marginBottom: '20px' }} />
          <Typography variant="h5" gutterBottom style={{ color: '#d32f2f' }}>
            Error
          </Typography>
          <Alert severity="error" style={{ marginTop: '20px', marginBottom: '20px' }}>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate('/')}
            style={{ marginTop: '20px' }}
          >
            Go to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" style={{ marginTop: '100px' }}>
      <Paper style={{ padding: '40px', textAlign: 'center' }}>
        <CancelIcon color="warning" style={{ fontSize: 64, marginBottom: '20px' }} />
        <Typography variant="h5" gutterBottom>
          Invitation Declined
        </Typography>
        <Alert severity="info" style={{ marginTop: '20px', marginBottom: '20px' }}>
          You have successfully declined the team invitation.
        </Alert>
        <Typography variant="body2" color="text.secondary" style={{ marginTop: '20px' }}>
          If you change your mind, you can ask the team administrator to send you a new invitation.
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/')}
          style={{ marginTop: '20px' }}
        >
          Go to Home
        </Button>
      </Paper>
    </Container>
  );
};

export default TeamInvitationReject;

